import {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Transition,
} from "@headlessui/react";
import {
    Archive,
    ArchiveRestore,
    ArrowUpRightIcon,
    EditIcon,
    MoreHorizontal,
    MoreVertical,
    Trash2Icon,
} from "lucide-react";
import { IconButton } from "@/components/Button/IconButton";
import { IUserJob } from "@/types/userJob";

interface JobActionsMenuProps {
    job: IUserJob;
    showArchived: boolean;
    onEditJob: (job: IUserJob) => void;
    onArchiveJob: (job: IUserJob) => void;
    onDeleteJob: (job: IUserJob) => void;
    isLoading?: boolean;
    variant?: "desktop" | "mobile";
}

export const JobActionsMenu = ({
    job,
    showArchived,
    onEditJob,
    onArchiveJob,
    onDeleteJob,
    isLoading = false,
    variant = "desktop",
}: JobActionsMenuProps) => {
    const isDesktop = variant === "desktop";
    const IconComponent = isDesktop ? MoreVertical : MoreHorizontal;
    const iconSize = isDesktop ? 16 : 20;

    return (
        <Menu
            as="div"
            className="relative inline-block text-left"
        >
            {({ open }) => (
                <>
                    <MenuButton as="div">
                        {isDesktop ? (
                            <IconButton
                                color="grey"
                                aria-label="More actions"
                                className="h-8 w-8"
                                loading={isLoading}
                            >
                                <IconComponent size={iconSize} />
                            </IconButton>
                        ) : (
                            <button className="rounded-lg p-1 hover:bg-neutral-100">
                                <IconComponent
                                    size={iconSize}
                                    className="text-neutral-900"
                                />
                            </button>
                        )}
                    </MenuButton>
                    <Transition
                        show={open}
                        enter="transition duration-100 ease-out"
                        enterFrom="transform scale-95 opacity-0"
                        enterTo="transform scale-100 opacity-100"
                        leave="transition duration-75 ease-out"
                        leaveFrom="transform scale-100 opacity-100"
                        leaveTo="transform scale-95 opacity-0"
                    >
                        <MenuItems
                            modal={isDesktop}
                            anchor={
                                isDesktop
                                    ? { to: "top end", gap: 8 }
                                    : undefined
                            }
                            className={`z-50 flex w-40 transform flex-col overflow-hidden rounded-lg bg-neutral-50 shadow-lg ring-2 ring-neutral-300 focus:outline-none ${
                                !isDesktop
                                    ? "absolute right-0 origin-top-right"
                                    : ""
                            }`}
                        >
                            {job.jobPostingUrl && (
                                <MenuItem
                                    as="a"
                                    href={job.jobPostingUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex h-10 w-full cursor-pointer items-center gap-2 border-b-2 border-neutral-300 px-3 text-app-labelsbuttons-4-mobile hover:bg-neutral-100 md:text-app-labelsbuttons-4-desktop"
                                >
                                    <ArrowUpRightIcon className="h-5 w-5" />
                                    Job Listing
                                </MenuItem>
                            )}
                            {!showArchived && (
                                <MenuItem
                                    as="button"
                                    onClick={() => onEditJob(job)}
                                    className="flex h-10 w-full cursor-pointer items-center gap-2 border-b-2 border-neutral-300 px-3 text-app-labelsbuttons-4-mobile hover:bg-neutral-100 md:text-app-labelsbuttons-4-desktop"
                                >
                                    <EditIcon className="h-5 w-5" />
                                    Edit
                                </MenuItem>
                            )}
                            <MenuItem
                                as="button"
                                onClick={() => onArchiveJob(job)}
                                className="flex h-10 w-full cursor-pointer items-center gap-2 border-b-2 border-neutral-300 px-3 text-app-labelsbuttons-4-mobile hover:bg-neutral-100 md:text-app-labelsbuttons-4-desktop"
                            >
                                {showArchived ? (
                                    <ArchiveRestore className="h-5 w-5" />
                                ) : (
                                    <Archive className="h-5 w-5" />
                                )}
                                {showArchived ? "Restore" : "Archive"}
                            </MenuItem>
                            <MenuItem
                                as="button"
                                onClick={() => onDeleteJob(job)}
                                className="flex h-10 w-full cursor-pointer items-center gap-2 px-3 text-app-labelsbuttons-4-mobile hover:bg-neutral-100 md:text-app-labelsbuttons-4-desktop"
                            >
                                <Trash2Icon className="h-5 w-5" />
                                Delete
                            </MenuItem>
                        </MenuItems>
                    </Transition>
                </>
            )}
        </Menu>
    );
};
